import 'cordova-plugin-purchase';

import {
  AfterViewInit,
  Component,
  HostListener,
  OnChanges,
  SimpleChange,
  SimpleChanges,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { IonToast } from '@ionic/angular/standalone';
import { find } from 'lodash';
import { Observable, concatMap, map, of, tap, throwError } from 'rxjs';
import { Session } from '@providers/session/session.service';
import { EventService } from '../../providers/event/event.service';
import { Logger } from '../../utils/logger';
import { PRODUCTS } from './in-app-purchase.config';

/**
 * @description In App Purchase.
 * @exports
 * @class InAppPurchaseComponent
 */
@Component({
  selector: 'app-in-app-purchase',
  templateUrl: 'in-app-purchase.component.html',
  imports: [IonToast]
})
export class InAppPurchaseComponent implements AfterViewInit, OnChanges {
  private readonly event = inject(EventService);
  private readonly session = inject(Session);

  private readonly toast = viewChild.required(IonToast);

  /**
   * A product code, as defined in the Product interface.
   */
  readonly code = input<string>();
  readonly disabled = input<boolean>();
  id = input.required<string>();

  readonly clicked = output<boolean>();
  readonly loading = output<boolean>();
  readonly purchased = output<any>();

  private readonly ongoingOrderIdToPurchase = signal<string | null>(null);

  private readonly logger = Logger.getInstance(InAppPurchaseComponent.name);

  product: CdvPurchase.Product | undefined;

  /**
   * Creates an instance of InAppPurchaseComponent.
   *
   * @memberof InAppPurchaseComponent
   */
  constructor() {
    this.product = undefined;
  }

  /**
   * @description Click.
   * @memberof InAppPurchaseComponent
   */
  @HostListener('click') click(): void {
    if (this.disabled() === true) return;

    this.clicked.emit(true);

    document.addEventListener(
      'deviceready',
      () => {
        this.clicked.emit(true);
        this.submit();
      },
      false
    );
  }

  /**
   * On changes.
   *
   * @param {SimpleChanges} changes Changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const id = changes['id'] as SimpleChange | undefined;
    if (id && id.currentValue !== id.previousValue) {
      document.addEventListener('deviceready', this.initialize.bind(this), {
        once: true
      });
    }
  }

  /**
   * @description After init.
   * @memberof InAppPurchaseComponent
   */
  ngAfterViewInit(): void {
    document.addEventListener('deviceready', this.initialize.bind(this), false);

    this.event.listen('IN_APP_PURCHASE_SUCCESS' as any).subscribe((payload) => {
      this.purchased.emit(payload);
    });
  }

  /**
   * Generate unique id.
   *
   * @returns {string}
   */
  generateUniqueId(): string {
    return (
      Date.now().toString(36) + Math.random().toString(36).substring(2, 10)
    );
  }

  /**
   * @description Initialize.
   * @memberof InAppPurchaseComponent
   */
  initialize(): void {
    const logger = this.logger.child(this.initialize.name);

    logger.log('this.id()', this.id());
    const product = find(PRODUCTS, (o) => o.code === this.id());
    logger.debug(`InAppPurchaseComponent . initialize . product`, product);
    const id = product?.id;
    if (id) {
      logger.log(`[CdvPurchase] Get product with id="${id}"`);
      const IAPProduct = CdvPurchase.store.get(id);
      logger.debug(
        `InAppPurchaseComponent . initialize . IAPProduct`,
        IAPProduct
      );
      if (IAPProduct) {
        logger.log(`[CdvPurchase] Found product with id="${id}"`, {
          product: IAPProduct
        });

        this.product = IAPProduct;
      } else {
        logger.log(`[CdvPurchase] Product with id="${id}" not found`);
      }
    }
  }

  /**
   * @description Create order.
   * @param {CdvPurchase.Product} product Product.
   * @returns {Observable<boolean>}
   * @memberof InAppPurchaseComponent
   */
  order(product: CdvPurchase.Product): Observable<boolean> {
    const orderId = this.generateUniqueId();
    return of(true).pipe(
      map(() => product.getOffer()),
      concatMap((offer) => {
        if (offer) {
          if (offer.canPurchase) {
            this.ongoingOrderIdToPurchase.set(orderId);
            return offer.order();
          }
          return throwError(() => new Error(`Product can't be purchased.`));
        }
        return throwError(() => new Error(`Product offer not found.`));
      }),
      tap(() => {
        this.ongoingOrderIdToPurchase.set(null);
      }),
      map(() => true)
    );
  }

  /**
   * @description Owned product.
   * @param {CdvPurchase.Product} product Product.
   * @returns {Observable<boolean>}
   * @memberof InAppPurchaseComponent
   */
  owned(product: CdvPurchase.Product): Observable<boolean> {
    const logger = this.logger.child(this.owned.name);

    return of(true).pipe(
      map(() => CdvPurchase.store.findInLocalReceipts(product)),
      concatMap((transaction) => {
        logger.log(`[CdvPurchase] Product id="${product.id}" transaction`, {
          transaction
        });
        if (transaction) {
          if (transaction.transactionId) {
            if (
              transaction.state === CdvPurchase.TransactionState.APPROVED ||
              transaction.state === CdvPurchase.TransactionState.FINISHED
            ) {
              this.event.publish(`IN_APP_PURCHASE_OWNED` as any, transaction);

              return of(true);
            }
          }
        }
        return this.order(product);
      }),
      map(() => true)
    );
  }

  /**
   * @description Submit.
   * @memberof InAppPurchaseComponent
   */
  submit(): void {
    const logger = this.logger.child(this.submit.name);

    of(true)
      .pipe(
        tap(() => {
          this.loading.emit(true);
        }),
        map(() => this.product),
        concatMap((product) => {
          if (product) {
            if (product.type === CdvPurchase.ProductType.NON_CONSUMABLE) {
              if (product.owned) {
                return this.owned(product);
              }
            }
            return this.order(product);
          }
          return throwError(() => new Error(`Product not found.`));
        })
      )
      .subscribe({
        /**
         * @description Success.
         */
        next: () => {
          this.loading.emit(false);
        },
        /**
         * @description Failed.
         * @param {unknown} reject Error.
         */
        error: (reject) => {
          logger.error(
            `[CdvPurchase] Product id="${this.product?.id}" purchase failed`,
            reject
          );

          let message = reject;
          if (reject instanceof Error) {
            message = reject.message;
          }

          this.loading.emit(false);

          const toast = this.toast();
          toast.message = message;
          toast.color = 'danger';
          toast.present();
        },
        /**
         * @description Complete.
         */
        complete: () => {
          this.loading.emit(false);
        }
      });
  }
}
